import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getOpenStatus, formatOpenStatus } from '@/lib/openingHours';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type DayHours = { open: string; close: string } | null;
type OpeningHours = Record<string, DayHours>;

interface OpenStatusBadgeProps {
  openingHours: OpeningHours | null | undefined;
  compact?: boolean;
  showDetail?: boolean;
}

export const OpenStatusBadge = ({ 
  openingHours, 
  compact = false,
  showDetail = false 
}: OpenStatusBadgeProps) => {
  const [now, setNow] = useState(new Date());

  // Update every minute to keep status current
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!openingHours || Object.keys(openingHours).length === 0) {
    return null;
  }

  const status = getOpenStatus(openingHours, now);
  const formatted = formatOpenStatus(status);

  const variantStyles = {
    open: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    closing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    closed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs',
        variantStyles[formatted.variant]
      )}
    >
      <Clock className={cn(compact ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
      {formatted.label}
    </span>
  );

  if (showDetail && formatted.detail) {
    return (
      <div className="flex flex-col gap-0.5">
        {badge}
        <span className="text-[10px] text-muted-foreground pl-1">
          {formatted.detail}
        </span>
      </div>
    );
  }

  if (formatted.detail) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {badge}
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{formatted.detail}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
};
